import { useTranslation } from "@/common/providers/i18n-provider";
import { getHtmlLang } from "@/config/locales";
import type { CsvOutputMode } from "@/lib/csv-to-beancount";
import type {
  StatementReconciliationReason,
  StatementReconciliationResult,
  StatementReconciliationStatus,
} from "@/lib/csv-statement-reconciliation";
import { formatDateLocalized, formatNumberLocalized } from "@/lib/format";
import type { CsvStatementFormState } from "./csv-statement-form";

interface CsvStatementPanelProps {
  value: CsvStatementFormState;
  onChange: (next: CsvStatementFormState) => void;
  /** Converter commodity — shown as context; statement balances use it. */
  currency: string;
  outputMode: CsvOutputMode;
  /**
   * Live reconciliation for the current conversion snapshot. Undefined while
   * statement options are omitted (panel off) or the emitter has not run.
   */
  reconciliation: StatementReconciliationResult | undefined;
  /** True while deferred CSV input has not caught up — hide matched claims. */
  pending: boolean;
  /** True when the current artifact includes statement directives. */
  statementReconciled: boolean;
}

type FieldKey = "startDate" | "endDate" | "opening" | "closing";

const FIELD_IDS: Record<FieldKey, string> = {
  startDate: "csv-statement-start",
  endDate: "csv-statement-end",
  opening: "csv-statement-opening",
  closing: "csv-statement-closing",
};

const ERROR_IDS: Record<FieldKey, string> = {
  startDate: "csv-statement-start-error",
  endDate: "csv-statement-end-error",
  opening: "csv-statement-opening-error",
  closing: "csv-statement-closing-error",
};

/**
 * Optional statement window + signed balances for the CSV converter.
 * Keeps every financial input in component memory — never URLs, storage,
 * analytics or network payloads.
 */
export function CsvStatementPanel({
  value,
  onChange,
  currency,
  outputMode,
  reconciliation,
  pending,
  statementReconciled,
}: CsvStatementPanelProps) {
  const { t, locale } = useTranslation();
  const statusId = "csv-statement-status";
  const currencyHintId = "csv-statement-currency";
  const openingModeHintId = "csv-statement-opening-mode";

  const displayStatus = pending
    ? ("pending" as const)
    : (reconciliation?.status ?? "disabled");
  const fieldErrors = pending
    ? {}
    : fieldErrorsFor(value, reconciliation?.reason);

  function patch(partial: Partial<CsvStatementFormState>) {
    onChange({ ...value, ...partial });
  }

  return (
    <fieldset style={{ border: 0, padding: 0, margin: 0 }}>
      <legend className="tool-settings-heading">
        {t("toolsCsvStatement.title")}
      </legend>
      <p className="tool-local-note">
        {t("toolsCsvStatement.description")}
      </p>
      <label className="tool-checkbox">
        <input
          type="checkbox"
          checked={value.enabled}
          onChange={(event) => patch({ enabled: event.target.checked })}
          className="mt-1"
        />
        <span className="tool-label-text">
          {t("toolsCsvStatement.enable")}
        </span>
      </label>

      {value.enabled ? (
        <>
          <p id={currencyHintId} className="tool-local-note">
            {t("toolsCsvStatement.currencyContext", {
              currency: currency.trim() || "USD",
            })}
          </p>
          <p id={openingModeHintId} className="tool-local-note">
            {t(
              outputMode === "complete"
                ? "toolsCsvStatement.openingHint.complete"
                : "toolsCsvStatement.openingHint.append",
            )}
          </p>
          <ul className="tool-local-note" style={{ margin: "0.75rem 0", paddingInlineStart: "1.25rem" }}>
            <li>{t("toolsCsvStatement.help.currency")}</li>
            <li>{t("toolsCsvStatement.help.liability")}</li>
            <li>{t("toolsCsvStatement.help.dates")}</li>
            <li>{t("toolsCsvStatement.help.errors")}</li>
            <li>{t("toolsCsvStatement.help.modes")}</li>
            <li>{t("toolsCsvStatement.help.bea")}</li>
          </ul>
          <div className="tool-settings-grid">
            {(
              [
                {
                  field: "startDate" as const,
                  labelKey: "toolsCsvStatement.startDate",
                  value: value.startDateText,
                  onChange: (next: string) => patch({ startDateText: next }),
                  inputMode: "text" as const,
                },
                {
                  field: "endDate" as const,
                  labelKey: "toolsCsvStatement.endDate",
                  value: value.endDateText,
                  onChange: (next: string) => patch({ endDateText: next }),
                  inputMode: "text" as const,
                },
                {
                  field: "opening" as const,
                  labelKey: "toolsCsvStatement.opening",
                  value: value.openingText,
                  onChange: (next: string) => patch({ openingText: next }),
                  inputMode: "decimal" as const,
                },
                {
                  field: "closing" as const,
                  labelKey: "toolsCsvStatement.closing",
                  value: value.closingText,
                  onChange: (next: string) => patch({ closingText: next }),
                  inputMode: "decimal" as const,
                },
              ] as const
            ).map(({ field, labelKey, value: fieldValue, onChange: setField, inputMode }) => {
              const issue = fieldErrors[field];
              return (
                <div key={field}>
                  <label className="tool-control-label" htmlFor={FIELD_IDS[field]}>
                    <span className="tool-label-text">{t(labelKey)}</span>
                    <input
                      id={FIELD_IDS[field]}
                      value={fieldValue}
                      onChange={(event) => setField(event.target.value)}
                      spellCheck={false}
                      dir="ltr"
                      inputMode={inputMode}
                      aria-invalid={issue !== undefined || undefined}
                      aria-describedby={
                        [
                          currencyHintId,
                          field === "opening" || field === "closing"
                            ? openingModeHintId
                            : undefined,
                          issue !== undefined ? ERROR_IDS[field] : undefined,
                        ]
                          .filter(Boolean)
                          .join(" ") || undefined
                      }
                      className="tool-control tool-account"
                    />
                  </label>
                  {issue !== undefined ? (
                    <span
                      id={ERROR_IDS[field]}
                      className="tool-notice"
                      role="alert"
                    >
                      {t(issue)}
                    </span>
                  ) : null}
                </div>
              );
            })}
          </div>

          <div
            id={statusId}
            className="tool-notes"
            role="status"
            aria-live="polite"
            aria-atomic="true"
          >
            <p className="tool-status">
              {statusHeadline(t, displayStatus, reconciliation, locale)}
            </p>
            {!pending &&
            reconciliation &&
            (reconciliation.status === "matched" ||
              reconciliation.status === "mismatch") &&
            reconciliation.openingText !== undefined &&
            reconciliation.netMovementText !== undefined &&
            reconciliation.expectedClosingText !== undefined &&
            reconciliation.enteredClosingText !== undefined &&
            reconciliation.differenceText !== undefined ? (
              <dl className="tool-local-note" style={{ margin: 0 }}>
                {(
                  [
                    {
                      labelKey: "toolsCsvStatement.summary.opening",
                      amount: reconciliation.openingText,
                    },
                    {
                      labelKey: "toolsCsvStatement.summary.movement",
                      amount: reconciliation.netMovementText,
                    },
                    {
                      labelKey:
                        "toolsCsvStatement.summary.expectedClosing",
                      amount: reconciliation.expectedClosingText,
                    },
                    {
                      labelKey:
                        "toolsCsvStatement.summary.enteredClosing",
                      amount: reconciliation.enteredClosingText,
                    },
                    {
                      labelKey: "toolsCsvStatement.summary.difference",
                      amount: reconciliation.differenceText,
                    },
                  ] as const
                ).map(({ labelKey, amount }) => (
                  <SummaryRow
                    key={labelKey}
                    label={t(labelKey)}
                    amount={amount}
                    currency={reconciliation.currency ?? currency}
                    locale={locale}
                    scale={reconciliation.scale}
                  />
                ))}
              </dl>
            ) : null}
            {!pending &&
            reconciliation?.reason &&
            reconciliation.status !== "matched" ? (
              <p className="tool-notice" role="alert">
                {t(reasonKey(reconciliation.reason))}
              </p>
            ) : null}
            {!pending && value.enabled && statementReconciled ? (
              <p className="tool-local-note">
                {t("toolsCsvStatement.download.reconciled")}
              </p>
            ) : null}
            {!pending &&
            value.enabled &&
            !statementReconciled &&
            reconciliation?.reason ? (
              <p className="tool-local-note">
                {t("toolsCsvStatement.download.unavailable", {
                  reason: t(reasonKey(reconciliation.reason)),
                })}
              </p>
            ) : null}
            {!pending &&
            value.enabled &&
            !statementReconciled &&
            reconciliation?.status === "mismatch" ? (
              <p className="tool-local-note">
                {t("toolsCsvStatement.download.ordinary")}
              </p>
            ) : null}
          </div>
        </>
      ) : null}
    </fieldset>
  );
}

function SummaryRow({
  label,
  amount,
  currency,
  locale,
  scale,
}: {
  label: string;
  amount: string;
  currency: string;
  locale: string;
  scale?: number;
}) {
  return (
    <div style={{ display: "flex", justifyContent: "space-between", gap: "1rem" }}>
      <dt>{label}</dt>
      <dd dir="ltr" style={{ margin: 0 }}>
        {formatBalanceLocalized(amount, locale, scale)} {currency}
      </dd>
    </div>
  );
}

/** Format authoritative fixed-point text with locale grouping; never re-round. */
function formatBalanceLocalized(
  fixedText: string,
  locale: string,
  scale?: number,
): string {
  const text = fixedText.trim();
  const match = /^(-?)(\d+)\.(\d+)$/.exec(text);
  if (!match) return text;
  const neg = match[1] === "-";
  const intPart = match[2];
  const frac = match[3];
  const digits = scale ?? frac.length;
  try {
    const formatter = new Intl.NumberFormat(getHtmlLang(locale), {
      minimumFractionDigits: digits,
      maximumFractionDigits: digits,
      useGrouping: true,
    });
    // Build from integer scaled value so we never pass a float residual.
    const scaled = BigInt(intPart + frac.padEnd(digits, "0").slice(0, digits));
    const asNumber = Number(scaled) / 10 ** digits;
    if (!Number.isSafeInteger(Number(scaled)) || !Number.isFinite(asNumber)) {
      return text;
    }
    const formatted = formatter.format(neg ? -asNumber : asNumber);
    return formatted;
  } catch {
    return text;
  }
}

const REASON_KEYS: Record<StatementReconciliationReason, string> = {
  not_enabled: "toolsCsvStatement.reason.notEnabled",
  missing_fields: "toolsCsvStatement.reason.missingFields",
  empty_selection: "toolsCsvStatement.reason.emptySelection",
  invalid_dates: "toolsCsvStatement.reason.invalidDates",
  date_order: "toolsCsvStatement.reason.dateOrder",
  row_outside_window: "toolsCsvStatement.reason.rowOutsideWindow",
  invalid_opening: "toolsCsvStatement.reason.invalidOpening",
  invalid_closing: "toolsCsvStatement.reason.invalidClosing",
  invalid_currency: "toolsCsvStatement.reason.invalidCurrency",
  mixed_currency: "toolsCsvStatement.reason.mixedCurrency",
  row_errors: "toolsCsvStatement.reason.rowErrors",
  non_finite: "toolsCsvStatement.reason.nonFinite",
  invalid_source: "toolsCsvStatement.reason.invalidSource",
  balance_mismatch: "toolsCsvStatement.reason.balanceMismatch",
};

function reasonKey(reason: StatementReconciliationReason): string {
  return REASON_KEYS[reason];
}

function statusHeadline(
  t: (key: string, substitutions?: Record<string, string>) => string,
  status: StatementReconciliationStatus | "pending",
  reconciliation: StatementReconciliationResult | undefined,
  locale: string,
): string {
  if (status === "pending") {
    return t("toolsCsvStatement.status.pending");
  }
  switch (status) {
    case "incomplete":
      return t("toolsCsvStatement.status.incomplete");
    case "invalid":
      return t("toolsCsvStatement.status.invalid");
    case "unsupported":
      return t("toolsCsvStatement.status.unsupported");
    case "matched": {
      const start = reconciliation?.startDate
        ? (formatDateLocalized(reconciliation.startDate, locale) ??
          reconciliation.startDate)
        : "";
      const end = reconciliation?.endDate
        ? (formatDateLocalized(reconciliation.endDate, locale) ??
          reconciliation.endDate)
        : "";
      return t("toolsCsvStatement.status.matched", {
        start,
        end,
        count: formatNumberLocalized(
          reconciliation?.selectedCount ?? 0,
          locale,
        ),
      });
    }
    case "mismatch":
      return t("toolsCsvStatement.status.mismatch", {
        difference: reconciliation?.differenceText ?? "",
        currency: reconciliation?.currency ?? "",
      });
    case "disabled":
    default:
      return t("toolsCsvStatement.status.incomplete");
  }
}

function fieldErrorsFor(
  value: CsvStatementFormState,
  reason: StatementReconciliationReason | undefined,
): Partial<Record<FieldKey, string>> {
  if (!reason) return {};
  switch (reason) {
    case "missing_fields": {
      const errors: Partial<Record<FieldKey, string>> = {};
      if (!value.startDateText.trim()) {
        errors.startDate = "toolsCsvStatement.errors.required";
      }
      if (!value.endDateText.trim()) {
        errors.endDate = "toolsCsvStatement.errors.required";
      }
      if (!value.openingText.trim()) {
        errors.opening = "toolsCsvStatement.errors.required";
      }
      if (!value.closingText.trim()) {
        errors.closing = "toolsCsvStatement.errors.required";
      }
      return errors;
    }
    case "invalid_dates":
      return {
        startDate: "toolsCsvStatement.errors.invalidDate",
        endDate: "toolsCsvStatement.errors.invalidDate",
      };
    case "date_order":
      return {
        startDate: "toolsCsvStatement.errors.dateOrder",
        endDate: "toolsCsvStatement.errors.dateOrder",
      };
    case "invalid_opening":
      return { opening: "toolsCsvStatement.errors.invalidAmount" };
    case "invalid_closing":
      return { closing: "toolsCsvStatement.errors.invalidAmount" };
    default:
      return {};
  }
}
